import { ServiceHelper } from 'src/app/core/helper/services.helper';
import { Product } from '../ecommerce/product';
import { CategoryGroup } from './category-group';
import { Page, PageType } from './page';
import { AppSettings } from 'src/app/core/configs/app.settings';

/**
 * Category
 */
export class Category extends Page {

    static override className = 'Category';
    static override idField = 'categoryId';

    categoryId: number = 0;
    parentCategoryId: number;
    color: string;
    categoryGroupId: number;

    // this is a special property, as it's not in the database, but we need
    // to pass it up so API knows to set it on the page object.
    // Might not be needed once we have page/category passing up as single parent/child object though...
    pageType: PageType;

    // this is a bb property that we want to fully ignore
    imagePath: string;

    // Non-API
    subCatItems: Page[] = [];
    override categoryGroup: CategoryGroup; // set on category page so category page block can access
    category: Category;

    // child item objects
    products: Product[] = [];

    // helped thingy to simplify matching categories on filtering. This gets set
    // while processing categories hiererchies in Category Group
    allChildCatIds: number[] = [];
    indentedTitle: string; // for rendering in select

    //set when we parse hierarchy
    hasChildren: boolean = false;

    // these are legacy BB properties 
    get startTime() { return this.openAt };
    get endTime() { return this.closeAt };
    set startTime(v) { }
    set endTime(v) { }

    constructor(cat?: Partial<Category>) {
        super();
        Object.assign(this, cat);
        super.init();

        this.imagePath = null;

        // map sub items to objects based on type
        this.subItems.forEach(subCatItem => {
            if (subCatItem.pgType == PageType.Product) {
                this.products.push(subCatItem.product);
            }
        });

        // set up indented title if it's null
        if (this.indentedTitle == null) {
            this.indentedTitle = this.title;
            if (this.parentCategoryId != null) {
                this.indentedTitle = ' - ' + this.indentedTitle;
            }
        }
    }

    override hasChanges() {
        return Category.hasChanges(Category);
    }
    override revert() {
        return Category.revert(Category);
    }

    // ----------------------------------------------------
    // Service-like features from here on down
    // ----------------------------------------------------

    // keep local cache of last accessed object, both a copy that is never modified unless saved,
    // and a 'working copy' that is edited in screens.
    protected static override _cachedServerObj: Category; // unmodified until saved
    protected static override _cachedObj: Category; // working copy
    protected static override _cacheExpiry: number;

    public static override async load(id: number) {
        return await Category.getById(Category, id);
    }

    public static async getCatForPage(id: number) {
        return await Category.getObject(Category, null, 'categoryForPage/' + id);
    }
    public static async getCatGroupForPage(id: number) {
        return await Category.getObject(Category, null, 'categoryGroupForPage/' + id);
    }

    public static async fetchForCategoryGroupId(catGroupId: number) {
        const r = await this.fetchChildrenFor(Category, catGroupId, 'categories', `forgroup/${catGroupId}`);
        return r;
    }

    public static async fetchPopulatedCategoriesForCategoryGroupId(catGroupId: number) {
        const r = await this.fetchChildrenFor(Category, catGroupId, 'categories', `populated/group/${catGroupId}`);
        return r;
    }

    public static async fetchChildrenForCategory(categoryId: number, silent: boolean = false) {
        return await this.fetchChildrenFor(Category, categoryId, 'categories', `${categoryId}/subitems`, silent);
    }

    static override async updateSortOrder(features: Category[]) {
        await ServiceHelper.httpSvc.put(features, `${AppSettings.apiURL}/categories/sortorder`, 'json');
    }

    public async fetchChildren(silent: boolean = false) {
        return await Category.fetchChildrenForCategory(this.categoryId, silent);
    }
}
